import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { AlertService } from '@shared/services/alert.service';
import { InputConfig } from '@shared/models/input-config.model';
import { INPUT_CONFIGS } from '@shared/constants';
import { SpinnerService } from '@shared/services/spinner.service';
import { ResetPasswordService } from '@login/services/reset-password.service';
import { ApiConstants } from '@login/util/api-constants';
import { RegexService, RegEx } from '@shared/services/regex.service';
import { ChallengeQuestionService } from '@login/services/challenge-question.service';

import {
  CONFIRM_PASSWORD_VALIDATORS,
  PASSWORD_VALIDATORS,
} from './reset-password.constants';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
})
export class ResetPasswordComponent implements OnInit {
  isPasswordReset = false;
  stateToken: string;

  passwordResetForm: UntypedFormGroup;
  passwordInputConfig: InputConfig;
  confirmPasswordInputConfig: InputConfig;

  constructor(
    private resetPasswordService: ResetPasswordService,
    private challengeQuestionService: ChallengeQuestionService,
    private spinnerService: SpinnerService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    private regexService: RegexService,
  ) {
    this.stateToken = this.challengeQuestionService.stateToken;
  }

  ngOnInit() {
    const passwordRegex = this.regexService.get(RegEx.PASSWORD_REGEX);

    this.passwordResetForm = this.fb.group({
      password: ['', PASSWORD_VALIDATORS(passwordRegex)],
      confirmPassword: ['', CONFIRM_PASSWORD_VALIDATORS()],
    });

    this.passwordControl.valueChanges.subscribe({
      next: password => {
        this.confirmPasswordControl.setValidators(
          CONFIRM_PASSWORD_VALIDATORS(password),
        );

        this.confirmPasswordControl.updateValueAndValidity();
      },
    });

    this.passwordInputConfig = INPUT_CONFIGS.PASSWORD({
      form: this.passwordResetForm,
      label: 'New Password',
      name: 'password',
      trimLeadingAndTrailingWhitespace: true,
    });

    this.confirmPasswordInputConfig = INPUT_CONFIGS.PASSWORD({
      form: this.passwordResetForm,
      label: 'Confirm New Password',
      name: 'confirmPassword',
      trimLeadingAndTrailingWhitespace: true,
    });
  }

  onResetPassword() {
    this.spinnerService.start('Loading...');
    this.resetPasswordService
      .requestPasswordReset(this.stateToken, this.password)
      .subscribe(
        res => this.onResetPasswordSuccess(res),
        err => this.onResetPasswordError(err),
      );
  }

  private onResetPasswordSuccess(res) {
    if (res.errorCode) {
      this.onResetPasswordError({ error: res });
      return;
    }

    this.isPasswordReset = true;
    this.spinnerService.stop();
    this.alertService.clearAlert();
  }

  private onResetPasswordError(err) {
    let alertMessage;
    switch (err.error.errorCode) {
      case 'ERRPRS025':
        alertMessage = ApiConstants.INVALID_STATE_TOKEN_ERROR;
        break;
      case 'ERRPRS026':
        alertMessage = err.error.oktaErrorCauses;
        break;
      default:
        alertMessage = err.error.statusText;
        break;
    }
    this.alertService.setAlert(alertMessage);
    this.spinnerService.stop();
  }

  private get password() {
    return this.passwordResetForm.value.password;
  }

  private get passwordControl() {
    return this.passwordResetForm.get('password');
  }

  private get confirmPasswordControl() {
    return this.passwordResetForm.get('confirmPassword');
  }
}
